import { create } from "zustand";
import { AppError } from "../../shared/utils/error-handler";
import type { UpdateUserInfoRequest } from "../../domain/types/user";
import type { LandingUserInfoResponse } from "../../domain/types/shared";

interface UserState {
  // State
  profile: LandingUserInfoResponse | null;
  isLoading: boolean;
  error: AppError | null;

  // Actions
  fetchProfile: (userId: number) => Promise<void>;
  updateProfile: (request: UpdateUserInfoRequest) => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

export const useUserStore = create<UserState>((set) => ({
  // Initial state
  profile: null,
  isLoading: false,
  error: null,

  // Actions
  fetchProfile: async () => {
    const { userUseCase } = await import("../../di/container");
    set({ isLoading: true, error: null });

    try {
      const profile = await userUseCase.getUserInfo();
      set({
        profile,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: AppError.fromUnknown(error),
        isLoading: false,
      });
      throw error;
    }
  },
  updateProfile: async (request: UpdateUserInfoRequest) => {
    const { userUseCase } = await import("../../di/container");
    set({ isLoading: true, error: null });

    try {
      const updatedProfile = await userUseCase.updateUserInfo(request);
      set({
        profile: updatedProfile,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: AppError.fromUnknown(error),
        isLoading: false,
      });
      throw error;
    }
  },
  clearError: () => set({ error: null }),
  setLoading: (loading: boolean) => set({ isLoading: loading }),
}));
